/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soyun Lee (sylee2@rsupport.com)
 *
 * @description
 *
 * @created at Tue Apr 22 2025
 **/

import { useState } from "react";
import styled from "@emotion/styled";

import { Utility, downloadBlobData } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import { Column, Extensions, Icon, Row, Typography } from "@/frameworks/designStuff/components";

import { UnsupportPreview } from "@/frameworks/core/components";
import { useFileManager } from "@/frameworks/core/hooks";
import { useFileExtensions, isNotSupportPreview } from "@/frameworks/core/utils";
import { Maybe, useProperties } from "@/frameworks/libraries";

export default function ContentPreview(
	file: ChatServices.Format2.Elements.File & { onClick?: (file: ChatServices.Format2.Elements.File) => void } & {
		isSingleFile?: boolean;
	}
) {
	const [isDownloading, setIsDownloading] = useState(false);
	const properties = useProperties();
	const { getFileExtension } = useFileExtensions();
	const { downloadFiles } = useFileManager();
	const extension = getFileExtension(file.mime || "");

	const mimeType = file.mime;
	const isNoSupportPreview = mimeType && isNotSupportPreview({ mimeType });

	const handleDownloadFile = () => {
		setIsDownloading(true);
		downloadFiles({ id: file.id }).then((response) => {
			setIsDownloading(false);
			downloadBlobData(response as Blob, file.title || "");
		});
	};

	const showPreviewCondition =
		(file.isSingleFile && (file.docThumbnailPath || extension === "zip")) || file.videoThumbnailPath;

	const handleClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		file.onClick?.(file);
	};

	const renderPreviewCard = () => (
		<CardWrapper onClick={handleClick}>
			<FileImageContainer horizonAlign="center" verticalAlign="center">
				<Maybe test={extension !== "zip"} fallback={<Icon name="ImgFileboxTypeZip24" size={64} fill="none" />}>
					<FileImage
						src={`${properties.default.webDomain}${file.videoThumbnailPath || file.docThumbnailPath}`}
						alt="linkImage"
					/>
				</Maybe>
				<ExtensionBadge>
					<Extensions type={extension} />
				</ExtensionBadge>
			</FileImageContainer>
			<Typography typoSize="Title3" typoColor="neutral_80_100">
				{file.title}
			</Typography>
			<Typography typoSize="Body4" typoColor="neutral_60_100">
				{_T("용량 {{size}}", { size: file.displaySize })}
			</Typography>
		</CardWrapper>
	);

	const renderFileCard = () => (
		<FileWrapper gap={12} onClick={handleClick}>
			<Extensions type={extension} />
			<Column css={{ width: "calc(100% - 36px - 12px - 6px)" }}>
				<FileTitle>{file.title}</FileTitle>
				<FileSize>
					{_T("용량 {{size}}", {
						size: file.displaySize ?? Utility.formatBytes(file.size)
					})}
				</FileSize>
			</Column>
		</FileWrapper>
	);

	return (
		<>
			{showPreviewCondition ? renderPreviewCard() : renderFileCard()}
			{(isNoSupportPreview || isDownloading) && (
				<UnsupportPreview isDownloading={isDownloading} onClick={handleDownloadFile} isChat />
			)}
		</>
	);
}

const CardWrapper = styled(Column)`
	position: relative;
	border: 1px solid ${({ theme }) => theme.colors.neutral_30_100};
	border-radius: 8px;
	background-color: ${({ theme }) => theme.colors.neutral_00_68};
	padding: 8px 10px 6px 10px;
	width: 182px;
	cursor: zoom-in;
	span {
		width: 100%;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		text-align: start;
	}
	&:hover {
		background: rgba(50, 51, 56, 0.08);
		.download-wrapper {
			display: block;
		}
	}
`;

const FileImageContainer = styled(Column)`
	position: relative;
	width: 100%;
	height: 108px;
	border-radius: 8px;
	background: ${({ theme }) => theme.colors.accent_10_100};
	margin-bottom: 6px;
`;
const ExtensionBadge = styled(Row)`
	position: absolute;
	right: 4px;
	top: 4px;
`;

const FileImage = styled.img<{ src: string }>`
	width: 100%;
	height: 100%;
	border-radius: 8px;
	background-image: url(${({ src }) => src});
	background-repeat: no-repeat;
	background-size: cover;
	background-position: center;
	object-fit: cover;
	cursor: pointer;
`;

const FileWrapper = styled(Row)`
	width: 100%;
	max-width: 280px;
	height: 50px;
	border-radius: 4px;
	border: 1px solid #e1e3e8;
	background: ${({ theme }) => theme.colors.neutral_00_100};
	padding: 6px 10px;
	cursor: zoom-in;
	position: relative;
	align-items: center;
	align-self: stretch;
	* {
		cursor: zoom-in;
	}

	&:hover {
		background: rgba(50, 51, 56, 0.08);
		.download-wrapper {
			display: block;
		}
	}

	svg {
		width: 32px;
		height: 32px;
	}
`;

const FileTitle = styled(Typography)`
	width: 100%;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	font-size: 14px;
	font-style: normal;
	font-weight: 500;
	color: #2e2f32;
	line-height: 20px;
`;

const FileSize = styled(Typography)`
	color: #6a6d73;
	font-feature-settings:
		"liga" off,
		"clig" off;
	font-size: 13px;
	font-style: normal;
	font-weight: 400;
	line-height: 18px;
`;
